// Compact Offline Status Component for Navbar
import React from 'react';
import { useOfflineMode } from '../../hooks/useOfflineMode.js';
import { WifiIcon, EyeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Compact offline status indicator for the navbar
 */
export function NavbarOfflineStatus({ offlineData = null }) {
  // Only call the hook if offlineData is not provided
  const hookData = useOfflineMode();
  const { isOffline, readOnlyMode, capabilities } = offlineData || hookData;

  if (!isOffline) {
    return null;
  }

  const hasData = capabilities?.availableData?.length > 0;

  return (
    <div className="mr-2 flex items-center space-x-2 rounded-md border border-amber-200 bg-amber-100 px-3 py-1 text-amber-800">
      {hasData ? (
        <EyeIcon className="h-4 w-4 text-amber-600" />
      ) : (
        <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
      )}
      <div className="flex items-center space-x-1">
        <span className="text-xs font-medium">{hasData ? 'Read-only' : 'Offline'}</span>
        {hasData && <span className="text-xs opacity-75">(cached data)</span>}
      </div>
    </div>
  );
}

/**
 * Detailed offline status component with tooltip/hover info
 * This component accepts offline data as props to avoid multiple hook calls
 */
export function NavbarOfflineStatusDetailed({ offlineData = null }) {
  // Only call the hook if offlineData is not provided
  const hookData = useOfflineMode();
  const { isOffline, readOnlyMode, capabilities } = offlineData || hookData;

  if (!isOffline) {
    return null;
  }

  const hasData = capabilities?.availableData?.length > 0;
  const availableData = capabilities?.availableData || [];

  const tooltipContent = hasData
    ? `Available: ${availableData
        .map((data) => {
          const labels = {
            sessions: 'Sessions',
            availability: 'Availability',
            crews: 'Crews',
            users: 'Users',
            planning: 'Planning',
          };
          return labels[data] || data;
        })
        .join(', ')}`
    : 'No cached data available';

  return (
    <div
      className="mr-2 flex cursor-help items-center space-x-2 rounded-md border border-amber-200 bg-amber-100 px-3 py-1 text-amber-800"
      title={tooltipContent}
    >
      <div className="flex items-center space-x-1">
        {hasData ? (
          <EyeIcon className="h-4 w-4 text-amber-600" />
        ) : (
          <WifiIcon className="h-4 w-4 text-amber-600 opacity-50" />
        )}
        <span className="text-xs font-medium">
          {hasData ? 'Offline - Read-only' : 'Offline - Limited'}
        </span>
      </div>
    </div>
  );
}

/**
 * Offline Status Provider Component
 * Use this when you need to render multiple offline status components together
 * It calls the hook once and passes the data to child components
 */
export function OfflineStatusProvider({ children, variant = 'compact' }) {
  const offlineData = useOfflineMode();

  if (variant === 'detailed') {
    return <NavbarOfflineStatusDetailed offlineData={offlineData} />;
  }

  return <NavbarOfflineStatus offlineData={offlineData} />;
}
